// RECIPEAI1 (DEC-590; docs/build-briefs/RECIPEAI1-ai-recipe-import.md). The "Import recipes" screen on Settings, Recipes: choose a
// CSV or XLSX file, the AI says which column is which, check ONE recipe exactly as it will be saved, then import.
//
// Draws what the server sent. The server reads the file at every step (the browser sends it again each time and never parsed
// rows), the AI only chooses columns, and every recipe is created by the same code as the manual recipe form. Changing a column
// re-runs the example without another AI call. If AI import is off, not set up or over budget, the screen says so and stops.
//
// Reuses the INVIMP1 full-page overlay classes (inventory-import, ii-*), so it adds no CSS. An admin screen built touch-first:
// every target is at least 44px, nothing depends on hover, text is set with textContent only (names come from the person's file).

import {
  applyRecipeImport,
  mapRecipeImport,
  previewRecipeImport,
  recipeImportStatus,
  type RecipeImportApplyDto,
  type RecipeImportMapDto,
  type RecipeImportMappingDto,
  type RecipeImportPreviewDto,
} from "./api.ts";
import { buildCreateConfirm, importAcceptLabel } from "./createItemsConfirm.ts";
import { FILE_ACCEPT, errorText, fileRefusal, fileSizeText } from "./inventoryImportModel.ts";
import type { Session } from "./session.ts";

export const RECIPE_IMPORT_PERMISSION = "admin.menu_drink.manage";

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) throw new Error(`expected element for selector "${selector}"`);
  return element;
}
const rootEl = mustFind<HTMLElement>("#recipe-import-screen");

type Phase = "upload" | "check" | "done";
const PHASE_NUMBER: Record<Phase, number> = { upload: 1, check: 2, done: 3 };
const PHASE_LABEL: Record<Phase, string> = { upload: "Upload", check: "Check", done: "Done" };

let open = false;
let phase: Phase = "upload";
let getSessionFn: (() => Session | undefined) | undefined;
let onClose: ((imported: RecipeImportApplyDto | undefined) => void) | undefined;
let onOpenInventory: (() => void) | undefined;
let generation = 0;
let status: { available: boolean; message: string } | undefined;
let file: File | undefined;
let mapped: RecipeImportMapDto | undefined;
let mapping: RecipeImportMappingDto | undefined;
let preview: RecipeImportPreviewDto | undefined;
let exampleIndex = 0;
/** Per distinct unmatched ingredient: an item id, "create" (make a new item) or "skip". Kept across example re-runs. */
let resolutions: Record<string, string> = {};
let uploadId: string | undefined;
let result: RecipeImportApplyDto | undefined;
let busy = "";
let problem = "";
/** DEC-593: true while the list of items to be added is shown for approval. Nothing is created until the person approves it. */
let confirming = false;
/** The keys the person ticked on the approval list. Starts empty every time the list is shown: each item is approved on its own. */
let approvedKeys = new Set<string>();

function session(): Session | undefined {
  return getSessionFn?.();
}
function current(gen: number, token: string): boolean {
  return open && gen === generation && session()?.token === token;
}

interface ElOptions {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.cls !== undefined) node.className = options.cls;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.testid !== undefined) node.dataset["testid"] = options.testid;
  for (const [name, value] of Object.entries(options.attrs ?? {})) node.setAttribute(name, value);
  for (const child of children) node.append(child);
  return node;
}
function button(label: string, cls: string, testid: string, onTap: () => void, disabled = false): HTMLButtonElement {
  const node = el("button", { cls, text: label, testid, attrs: { type: "button" } });
  node.disabled = disabled;
  node.addEventListener("click", onTap);
  return node;
}

function clearState(): void {
  phase = "upload";
  status = undefined;
  file = undefined;
  mapped = undefined;
  mapping = undefined;
  preview = undefined;
  exampleIndex = 0;
  resolutions = {};
  uploadId = undefined;
  result = undefined;
  busy = "";
  problem = "";
  confirming = false;
}

export function openRecipeImport(options: {
  getSession: () => Session | undefined;
  onClose: (imported: RecipeImportApplyDto | undefined) => void;
  /** Leaves the import and opens Settings, Inventory items (the result links there when items were created). */
  onOpenInventory?: () => void;
}): void {
  generation += 1;
  getSessionFn = options.getSession;
  onClose = options.onClose;
  onOpenInventory = options.onOpenInventory;
  clearState();
  open = true;
  rootEl.hidden = false;
  document.body.classList.add("inventory-import-open");
  render();
  void loadStatus();
}

function hide(): void {
  generation += 1;
  open = false;
  onClose = undefined;
  onOpenInventory = undefined;
  getSessionFn = undefined;
  clearState();
  rootEl.replaceChildren();
  rootEl.hidden = true;
  document.body.classList.remove("inventory-import-open");
}

function close(): void {
  const imported = result !== undefined && result.created > 0 ? result : undefined;
  const callback = onClose;
  hide();
  callback?.(imported);
}

/** Sign-out removes the screen. */
export function resetRecipeImport(): void {
  hide();
}

async function loadStatus(): Promise<void> {
  const active = session();
  if (active === undefined) return;
  const gen = generation;
  const response = await recipeImportStatus(active.apiBaseUrl, active.tenantId);
  if (!current(gen, active.token)) return;
  if (response.status === 200 && typeof response.body.available === "boolean") {
    status = { available: response.body.available, message: response.body.message ?? "" };
  } else {
    status = { available: false, message: errorText(response.status, response.body.message) };
  }
  render();
}

async function chooseFile(chosen: File, sheet?: string): Promise<void> {
  const active = session();
  if (active === undefined || busy !== "") return;
  const refusal = fileRefusal(chosen);
  if (refusal !== "") {
    problem = refusal;
    render();
    return;
  }
  const gen = generation;
  busy = "The AI is reading your file";
  problem = "";
  render();
  const response = await mapRecipeImport(active.apiBaseUrl, active.tenantId, chosen, sheet);
  if (!current(gen, active.token)) return;
  busy = "";
  if (response.status !== 200 || response.body.mapping === undefined) {
    problem = errorText(response.status, response.body.message);
    render();
    return;
  }
  file = chosen;
  mapped = response.body as RecipeImportMapDto;
  mapping = mapped.mapping;
  resolutions = {};
  uploadId = undefined;
  exampleIndex = 0;
  phase = "check";
  render();
  await runPreview();
}

async function runPreview(): Promise<void> {
  const active = session();
  if (active === undefined || file === undefined || mapped === undefined || mapping === undefined) return;
  const gen = generation;
  busy = "Checking";
  problem = "";
  render();
  const response = await previewRecipeImport(active.apiBaseUrl, active.tenantId, file, {
    sheet: mapped.sheet,
    mapping,
    example: exampleIndex,
  });
  if (!current(gen, active.token)) return;
  busy = "";
  if (response.status !== 200 || response.body.counts === undefined) {
    problem = errorText(response.status, response.body.message);
    preview = undefined;
    render();
    return;
  }
  preview = response.body as RecipeImportPreviewDto;
  mapping = preview.mapping;
  exampleIndex = preview.example?.index ?? 0;
  const keys = new Set(preview.unmatched.map((u) => u.key));
  for (const key of Object.keys(resolutions)) if (!keys.has(key)) delete resolutions[key];
  render();
}

/** The ingredients the person set to "Create it", by name. These are listed for approval and sent back as the approved list. */
function createCandidates(): Array<{ key: string; name: string; lineCount: number }> {
  return preview === undefined ? [] : preview.unmatched.filter((u) => resolutions[u.key] === "create");
}
function namesToCreate(): string[] {
  return createCandidates().filter((u) => approvedKeys.has(u.key)).map((u) => u.name);
}

function drawConfirm(body: HTMLElement): void {
  const { card, actions } = buildCreateConfirm({
    candidates: createCandidates().map((u) => ({
      key: u.key,
      name: u.name,
      leftOutNote: `${u.lineCount} ${u.lineCount === 1 ? "line" : "lines"} will be left out`,
    })),
    approved: approvedKeys,
    busy: busy !== "",
    testidPrefix: "recipe-import-confirm",
    intro: "They are not in your inventory yet. Tick each one you want added. You fill in size, cost and vendor later.",
    acceptLabel: (count) => (busy === "Importing" ? "Importing" : importAcceptLabel(count)),
    showLeftOut: true,
    onToggle: (key, ticked) => {
      if (ticked) approvedKeys.add(key);
      else approvedKeys.delete(key);
      render();
    },
    onBack: () => {
      confirming = false;
      render();
    },
    onAccept: () => void runApply(),
  });
  body.append(card, actions);
}

function unresolvedCount(): number {
  return preview === undefined ? 0 : preview.unmatched.filter((u) => resolutions[u.key] === undefined).length;
}

async function runApply(): Promise<void> {
  const active = session();
  if (active === undefined || file === undefined || mapped === undefined || mapping === undefined) return;
  if (busy !== "" || unresolvedCount() > 0) return;
  // Items are created only after the person has seen the list and approved it.
  const approvedCreate = namesToCreate();
  if (createCandidates().length > 0 && !confirming) return;
  // An ingredient left unticked is not created: its lines are skipped.
  const sent: Record<string, string> = { ...resolutions };
  for (const u of createCandidates()) if (!approvedKeys.has(u.key)) sent[u.key] = "skip";
  const gen = generation;
  uploadId = uploadId ?? crypto.randomUUID();
  busy = "Importing";
  problem = "";
  render();
  const response = await applyRecipeImport(
    active.apiBaseUrl,
    active.tenantId,
    file,
    { sheet: mapped.sheet, mapping, resolutions: sent, approvedCreate },
    uploadId,
  );
  if (!current(gen, active.token)) return;
  busy = "";
  if ((response.status !== 200 && response.status !== 201) || response.body.created === undefined) {
    confirming = false;
    problem = errorText(response.status, response.body.message);
    render();
    return;
  }
  result = response.body as RecipeImportApplyDto;
  phase = "done";
  render();
}

// ---------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------

function header(): HTMLElement {
  const head = el("div", { cls: "ii-head" });
  head.append(
    el("span", { cls: "ii-head-title", text: "Import recipes" }),
    el("span", { cls: "ii-head-sub", text: `${PHASE_NUMBER[phase]} of 3 ${PHASE_LABEL[phase]}`, testid: "recipe-import-step" }),
    el("span", { cls: "ii-sp" }),
  );
  if (phase !== "done") head.append(button("Cancel", "ii-ghost", "recipe-import-cancel", close));
  return head;
}

function render(): void {
  if (!open) return;
  rootEl.replaceChildren();
  const body = el("div", { cls: "ii-body" });
  rootEl.append(header(), body);
  if (problem !== "") {
    body.append(el("div", { cls: "ii-problem", text: problem, testid: "recipe-import-problem", attrs: { role: "alert" } }));
  }
  if (phase === "upload") drawUpload(body);
  else if (phase === "check") {
    if (confirming) drawConfirm(body);
    else drawCheck(body);
  }
  else drawDone(body);
}

function drawUpload(body: HTMLElement): void {
  if (status === undefined) {
    body.append(el("p", { cls: "ii-tert", text: "Checking", testid: "recipe-import-status" }));
    return;
  }
  if (!status.available) {
    body.append(
      el("div", { cls: "ii-card", testid: "recipe-import-unavailable" },
        el("div", { cls: "ii-head", text: "AI import is not available" }),
        el("div", { cls: "ii-tert", text: status.message }),
      ),
    );
    return;
  }
  const input = el("input", {
    cls: "ii-file-input",
    testid: "recipe-import-file-input",
    attrs: { type: "file", accept: FILE_ACCEPT, "aria-label": "Choose file" },
  });
  input.addEventListener("change", () => {
    const chosen = input.files?.[0];
    if (chosen !== undefined) void chooseFile(chosen);
    input.value = "";
  });
  const drop = el("div", { cls: "ii-drop", testid: "recipe-import-drop" });
  drop.append(
    button(busy === "" ? "Choose file" : busy, "ii-primary ii-primary-large", "recipe-import-choose", () => input.click(), busy !== ""),
    el("span", { cls: "ii-tert", text: "Or drop a file here. CSV or XLSX, up to 5 MB" }),
    input,
  );
  drop.addEventListener("dragover", (event) => event.preventDefault());
  drop.addEventListener("drop", (event) => {
    event.preventDefault();
    const dropped = event.dataTransfer?.files[0];
    if (dropped !== undefined && busy === "") void chooseFile(dropped);
  });
  body.append(
    drop,
    el("p", {
      cls: "ii-tert",
      text: "The AI sees the column headings and a few sample rows of your file. It does not see your items, staff, clients or payments.",
      testid: "recipe-import-privacy",
    }),
  );
}

function columnSelect(
  label: string,
  testid: string,
  value: number | null,
  required: boolean,
  onPick: (value: number | null) => void,
): HTMLElement {
  const wrap = el("label", { cls: "ii-map ii-map-stack" });
  wrap.append(el("span", { cls: "ii-head", text: label }));
  const select = el("select", { cls: "ii-select", testid, attrs: { "aria-label": label } });
  if (!required) select.append(new Option("Not in this file", ""));
  (mapped?.headers ?? []).forEach((heading, index) => {
    select.append(new Option(`${index + 1}. ${heading}`, String(index)));
  });
  select.value = value === null ? "" : String(value);
  select.disabled = busy !== "";
  select.addEventListener("change", () => {
    onPick(select.value === "" ? null : Number(select.value));
    exampleIndex = 0;
    void runPreview();
  });
  wrap.append(select);
  return wrap;
}

function drawMapping(card: HTMLElement): void {
  if (mapping === undefined || mapped === undefined) return;
  const m = mapping;
  card.append(el("div", { cls: "ii-title", text: "How we read your file" }));
  const layout = el("label", { cls: "ii-map ii-map-stack" });
  layout.append(el("span", { cls: "ii-head", text: "Each row is" }));
  const layoutSelect = el("select", { cls: "ii-select", testid: "recipe-import-layout", attrs: { "aria-label": "Each row is" } });
  layoutSelect.append(
    new Option("One ingredient of a recipe", "line_per_row"),
    new Option("One whole recipe", "recipe_per_row"),
  );
  layoutSelect.value = m.layout;
  layoutSelect.disabled = busy !== "";
  layoutSelect.addEventListener("change", () => {
    m.layout = layoutSelect.value === "recipe_per_row" ? "recipe_per_row" : "line_per_row";
    if (m.layout === "recipe_per_row" && m.ingredientGroups.length === 0 && m.ingredientText === null && m.ingredient !== null) {
      m.ingredientGroups = [{ item: m.ingredient, amount: m.amount, unit: m.unit }];
    }
    if (m.layout === "line_per_row" && m.ingredient === null) {
      const first = m.ingredientGroups[0];
      if (first !== undefined) {
        m.ingredient = first.item;
        m.amount = first.amount;
        m.unit = first.unit;
      }
    }
    exampleIndex = 0;
    void runPreview();
  });
  layout.append(layoutSelect);
  card.append(layout);
  card.append(columnSelect("Recipe name", "recipe-import-col-name", m.recipeName, true, (v) => (m.recipeName = v ?? m.recipeName)));
  card.append(columnSelect("Category", "recipe-import-col-category", m.category, false, (v) => (m.category = v)));
  card.append(columnSelect("Extra build charge", "recipe-import-col-charge", m.buildCharge, false, (v) => (m.buildCharge = v)));
  if (m.layout === "line_per_row") {
    card.append(columnSelect("Ingredient", "recipe-import-col-ingredient", m.ingredient, true, (v) => (m.ingredient = v ?? m.ingredient)));
    card.append(columnSelect("Amount", "recipe-import-col-amount", m.amount, false, (v) => (m.amount = v)));
    card.append(columnSelect("Unit", "recipe-import-col-unit", m.unit, false, (v) => (m.unit = v)));
  } else {
    card.append(columnSelect("Ingredient list in one cell", "recipe-import-col-text", m.ingredientText, false, (v) => (m.ingredientText = v)));
    m.ingredientGroups.forEach((group, index) => {
      card.append(
        el("div", { cls: "ii-head ii-gap", text: `Ingredient ${index + 1}` }),
        columnSelect("Name", `recipe-import-group-${index}-item`, group.item, true, (v) => (group.item = v ?? group.item)),
        columnSelect("Amount", `recipe-import-group-${index}-amount`, group.amount, false, (v) => (group.amount = v)),
        columnSelect("Unit", `recipe-import-group-${index}-unit`, group.unit, false, (v) => (group.unit = v)),
        button("Remove ingredient", "ii-ghost", `recipe-import-group-${index}-remove`, () => {
          m.ingredientGroups.splice(index, 1);
          if (m.ingredientGroups.length === 0 && m.ingredientText === null) {
            problem = "Choose the columns that hold the ingredients.";
            render();
            return;
          }
          void runPreview();
        }, busy !== ""),
      );
    });
    if (mapped.headers.length > m.ingredientGroups.length) {
      card.append(
        button("Add an ingredient", "ii-secondary", "recipe-import-group-add", () => {
          const used = new Set(m.ingredientGroups.map((g) => g.item));
          const next = mapped?.headers.findIndex((_, i) => !used.has(i)) ?? 0;
          m.ingredientGroups.push({ item: Math.max(0, next), amount: null, unit: null });
          void runPreview();
        }, busy !== "" || m.ingredientGroups.length >= 30),
      );
    }
  }
  if (preview !== undefined && preview.categories.length > 0) {
    const wrap = el("label", { cls: "ii-map ii-map-stack" });
    wrap.append(el("span", { cls: "ii-head", text: "Category when the file has none" }));
    const select = el("select", { cls: "ii-select", testid: "recipe-import-default-category", attrs: { "aria-label": "Category when the file has none" } });
    for (const c of preview.categories) select.append(new Option(c.label, c.value));
    select.value = m.defaultCategory ?? preview.categories.find((c) => c.value === "other")?.value ?? preview.categories[0]?.value ?? "";
    select.disabled = busy !== "";
    select.addEventListener("change", () => {
      m.defaultCategory = select.value;
      void runPreview();
    });
    wrap.append(select);
    card.append(wrap);
  }
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function drawExample(body: HTMLElement): void {
  const p = preview;
  const card = el("div", { cls: "ii-card", testid: "recipe-import-example" });
  card.append(el("div", { cls: "ii-title", text: "Example recipe" }));
  if (p === undefined) {
    card.append(el("div", { cls: "ii-tert", text: busy === "" ? "No example yet" : busy }));
    body.append(card);
    return;
  }
  const example = p.example;
  if (example === null) {
    card.append(el("div", { cls: "ii-tert", text: "No recipe in this file can be imported with these columns.", testid: "recipe-import-no-example" }));
    body.append(card);
    return;
  }
  card.append(
    el("div", { cls: "ii-head", text: example.name, testid: "recipe-import-example-name" }),
    el("div", {
      cls: "ii-tert",
      text: `${example.category}${example.categoryFromFile ? "" : " (chosen for you)"}${example.buildChargeCents > 0 ? `, extra charge ${money(example.buildChargeCents)}` : ""}`,
    }),
  );
  const list = el("ul", { attrs: { "aria-label": "Ingredients" } });
  for (const line of example.lines) {
    list.append(
      el("li", {
        testid: "recipe-import-example-line",
        text:
          line.problem !== null
            ? `${line.text === "" ? "(no name)" : line.text}  Needs a look: ${line.problem}. Left out of the recipe, add it by hand after.`
            : `${line.amount ?? ""} ${line.unit ?? ""}  ${line.text}  ${line.item !== null ? `Matched to ${line.item.name}` : resolutions[line.key] === "create" ? "New item" : "Not matched"}`,
      }),
    );
  }
  card.append(list);
  if (p.counts.recipesToImport > 1) {
    card.append(button("Show another example", "ii-secondary", "recipe-import-another", () => {
      exampleIndex += 1;
      void runPreview();
    }, busy !== ""));
  }
  body.append(card);
}

function drawCounts(body: HTMLElement): void {
  const p = preview;
  if (p === undefined) return;
  const c = p.counts;
  const card = el("div", { cls: "ii-card", testid: "recipe-import-counts" });
  card.append(
    el("div", { cls: "ii-title", text: "What will happen" }),
    el("div", { text: `${c.recipesFound} recipes found, ${c.recipesToImport} ready to import`, testid: "recipe-import-count-recipes" }),
    el("div", { text: `${c.lines} ingredient lines`, testid: "recipe-import-count-lines" }),
    ...(c.linesFlagged > 0
      ? [el("div", { text: `${c.linesFlagged} ${c.linesFlagged === 1 ? "line needs" : "lines need"} a look`, testid: "recipe-import-count-flagged" })]
      : []),
    el("div", { text: `${c.unmatchedNames} ingredients not matched to your items`, testid: "recipe-import-count-unmatched" }),
    el("div", { text: `${c.rowsSkipped} left out`, testid: "recipe-import-count-skipped" }),
  );
  if (p.skipped.length > 0) {
    const details = el("details", { testid: "recipe-import-skipped" });
    details.append(el("summary", { text: "See what is left out and why" }));
    const list = el("ul");
    for (const s of p.skipped) {
      list.append(el("li", { text: `${s.recipe ?? "Row"}${s.rowNumber === null ? "" : `, row ${s.rowNumber}`}: ${s.reason}` }));
    }
    details.append(list);
    if (p.skippedTruncated) details.append(el("div", { cls: "ii-tert", text: "Only the first 100 are listed." }));
    card.append(details);
  }
  if (p.flaggedLines.length > 0) {
    const details = el("details", { testid: "recipe-import-flagged" });
    details.append(el("summary", { text: "See the lines that need a look" }));
    const list = el("ul");
    for (const f of p.flaggedLines) {
      list.append(el("li", { text: `${f.recipe}, row ${f.rowNumber}: ${f.text === "" ? "(no name)" : f.text} ${f.reason}` }));
    }
    details.append(list);
    if (p.flaggedTruncated) details.append(el("div", { cls: "ii-tert", text: "Only the first 100 are listed." }));
    card.append(details);
  }
  body.append(card);
}

function drawUnmatched(body: HTMLElement): void {
  const p = preview;
  if (p === undefined || p.unmatched.length === 0) return;
  const card = el("div", { cls: "ii-card", testid: "recipe-import-unmatched" });
  card.append(
    el("div", { cls: "ii-title", text: "Match these ingredients" }),
    el("div", {
      cls: "ii-tert",
      text: "Create it adds a new item to your inventory with this name. Fill in its size, cost and vendor later. Or pick one of your items, or skip the lines.",
    }),
  );
  for (const u of p.unmatched) {
    const wrap = el("label", { cls: "ii-map ii-map-stack" });
    wrap.append(el("span", { cls: "ii-head", text: `${u.name} (${u.lineCount} ${u.lineCount === 1 ? "line" : "lines"})` }));
    const select = el("select", { cls: "ii-select", testid: "recipe-import-resolve", attrs: { "aria-label": `Item for ${u.name}` } });
    select.dataset["key"] = u.key;
    select.append(new Option("Choose", ""), new Option("Create it", "create"), new Option("Skip these lines", "skip"));
    if (u.candidates.length > 0) {
      const group = document.createElement("optgroup");
      group.label = "Suggested";
      for (const c of u.candidates) group.append(new Option(c.name, c.id));
      select.append(group);
    }
    const chosen = resolutions[u.key];
    const fillAll = (): void => {
      if (select.dataset["full"] === "1") return;
      select.dataset["full"] = "1";
      const group = document.createElement("optgroup");
      group.label = "All your items";
      for (const item of p.items) group.append(new Option(item.name, item.id));
      select.append(group);
      select.value = resolutions[u.key] ?? "";
    };
    if (chosen !== undefined && chosen !== "skip" && chosen !== "create" && !u.candidates.some((c) => c.id === chosen)) fillAll();
    select.value = chosen ?? "";
    select.addEventListener("focus", fillAll);
    select.addEventListener("mousedown", fillAll);
    select.addEventListener("change", () => {
      if (select.value === "") delete resolutions[u.key];
      else resolutions[u.key] = select.value;
      render();
    });
    wrap.append(select);
    card.append(wrap);
  }
  if (p.itemsTruncated) card.append(el("div", { cls: "ii-tert", text: "Only the first 3,000 items are listed." }));
  body.append(card);
}

function drawCheck(body: HTMLElement): void {
  const card = el("div", { cls: "ii-card", testid: "recipe-import-mapping" });
  drawMapping(card);
  body.append(card);
  drawExample(body);
  drawCounts(body);
  drawUnmatched(body);
  const unresolved = unresolvedCount();
  const count = preview?.counts.recipesToImport ?? 0;
  const actions = el("div", { cls: "ii-row ii-actions" });
  if (file !== undefined) {
    actions.append(el("span", { cls: "ii-tert", text: `${file.name}, ${fileSizeText(file.size)}` }));
  }
  actions.append(el("span", { cls: "ii-sp" }));
  if (unresolved > 0) {
    actions.append(el("span", { cls: "ii-blocked-reason", text: `Choose an item, create it, or skip for ${unresolved} more`, testid: "recipe-import-blocked" }));
  }
  actions.append(
    button(
      busy === "Importing" ? "Importing" : `Import ${count} ${count === 1 ? "recipe" : "recipes"}`,
      "ii-primary",
      "recipe-import-accept",
      () => {
        if (createCandidates().length > 0) {
          approvedKeys = new Set<string>();
          confirming = true;
          render();
        } else void runApply();
      },
      busy !== "" || preview === undefined || count === 0 || unresolved > 0,
    ),
  );
  body.append(actions);
}

function drawDone(body: HTMLElement): void {
  const r = result;
  if (r === undefined) return;
  const card = el("div", { cls: "ii-card", testid: "recipe-import-result" });
  card.append(
    el("div", {
      cls: "ii-title",
      text: r.created === 0 ? "No recipes were imported" : `${r.created} ${r.created === 1 ? "recipe" : "recipes"} imported`,
      testid: "recipe-import-result-headline",
    }),
  );
  const made = r.itemsCreated ?? 0;
  if (made > 0) {
    card.append(
      el("div", {
        text: `${made} new ${made === 1 ? "item" : "items"} created in Inventory items. Fill in their size, cost and vendor; recipe costs show as incomplete until then.`,
        testid: "recipe-import-result-items",
      }),
    );
  }
  if (r.skipped > 0) card.append(el("div", { text: `${r.skipped} left out`, testid: "recipe-import-result-skipped" }));
  if (r.linesSkipped > 0) card.append(el("div", { text: `${r.linesSkipped} ingredient lines skipped` }));
  const flagged = r.flaggedLines ?? [];
  if (flagged.length > 0) {
    const list = el("ul", { attrs: { "aria-label": "Lines to add by hand" }, testid: "recipe-import-result-flagged" });
    for (const f of flagged) list.append(el("li", { text: `${f.recipe}: ${f.text === "" ? "(no name)" : f.text} ${f.reason}. Add it in the recipe.` }));
    card.append(el("div", { cls: "ii-tert", text: "These lines were left out of their recipes. Add them by hand:" }), list);
  }
  const noSelection = r.liquorSelectionOff ?? [];
  if (noSelection.length > 0) {
    const list = el("ul", { attrs: { "aria-label": "Recipes without a liquor selection" }, testid: "recipe-import-result-no-liquor-selection" });
    for (const n of noSelection) list.append(el("li", { text: `${n.recipe}: ${n.reason}.` }));
    card.append(el("div", { cls: "ii-tert", text: "These recipes were saved without a liquor selection. Turn it on in the recipe if it should have one:" }), list);
  }
  if (r.skippedRecipes.length > 0) {
    const list = el("ul");
    for (const s of r.skippedRecipes) list.append(el("li", { text: `${s.recipe ?? "Row"}: ${s.reason}` }));
    card.append(list);
  }
  const actions = el("div", { cls: "ii-row ii-actions" }, el("span", { cls: "ii-sp" }));
  if (made > 0 && onOpenInventory !== undefined) {
    actions.append(
      button("Open Inventory items", "ii-secondary", "recipe-import-open-inventory", () => {
        const openInventory = onOpenInventory;
        close();
        openInventory?.();
      }),
    );
  }
  actions.append(button("Done", "ii-primary", "recipe-import-done", close));
  body.append(card, actions);
}
